<script setup>
import { computed, ref } from 'vue';
import { useIntervalFn } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { MESSAGE_STATUS } from './constants';

import Icon from 'next/icon/Icon.vue';

// Acuses robustos (Victor 2026-05-29): si llega un status no reconocido o vacío
// igual mostramos al menos 1 raya gris (sent). Así el agente nunca ve un msg
// sin palomita y sabe que el envío al menos salió del CRM.
const { status } = defineProps({
  status: {
    type: String,
    required: false,
    default: 'sent',
  },
});

const effectiveStatus = computed(() => {
  if (Object.values(MESSAGE_STATUS).includes(status)) return status;
  return MESSAGE_STATUS.SENT;
});

const { t } = useI18n();

const progresIconSequence = [
  'i-lucide-clock-1',
  'i-lucide-clock-2',
  'i-lucide-clock-3',
  'i-lucide-clock-4',
  'i-lucide-clock-5',
  'i-lucide-clock-6',
  'i-lucide-clock-7',
  'i-lucide-clock-8',
  'i-lucide-clock-9',
  'i-lucide-clock-10',
  'i-lucide-clock-11',
  'i-lucide-clock-12',
];

const progessIcon = ref(progresIconSequence[0]);

const rotateIcon = () => {
  const currentIndex = progresIconSequence.indexOf(progessIcon.value);
  const nextIndex = (currentIndex + 1) % progresIconSequence.length;
  progessIcon.value = progresIconSequence[nextIndex];
};

useIntervalFn(rotateIcon, 500, {
  immediate: effectiveStatus.value === MESSAGE_STATUS.PROGRESS,
  immediateCallback: false,
});

const statusIcon = computed(() => {
  const statusIconMap = {
    [MESSAGE_STATUS.SENT]: 'i-lucide-check',
    [MESSAGE_STATUS.DELIVERED]: 'i-lucide-check-check',
    [MESSAGE_STATUS.READ]: 'i-lucide-check-check',
  };

  return statusIconMap[effectiveStatus.value];
});

const statusColor = computed(() => {
  const statusIconMap = {
    [MESSAGE_STATUS.SENT]: 'text-n-slate-10',
    [MESSAGE_STATUS.DELIVERED]: 'text-n-slate-10',
    [MESSAGE_STATUS.READ]: 'text-[#7EB6FF]',
  };

  return statusIconMap[effectiveStatus.value];
});

const tooltipText = computed(() => {
  const statusTextMap = {
    [MESSAGE_STATUS.SENT]: t('CHAT_LIST.SENT'),
    [MESSAGE_STATUS.DELIVERED]: t('CHAT_LIST.DELIVERED'),
    [MESSAGE_STATUS.READ]: t('CHAT_LIST.MESSAGE_READ'),
    [MESSAGE_STATUS.PROGRESS]: t('CHAT_LIST.SENDING'),
  };

  return statusTextMap[effectiveStatus.value];
});

// Texto corto inline al lado del checkmark (más limpio que el tooltip).
const inlineLabel = computed(() => {
  const map = {
    [MESSAGE_STATUS.DELIVERED]: 'Entregado',
    [MESSAGE_STATUS.READ]: 'Leído',
  };
  return map[effectiveStatus.value];
});
</script>

<template>
  <span class="inline-flex items-center gap-1">
    <Icon
      v-if="effectiveStatus === MESSAGE_STATUS.PROGRESS"
      v-tooltip.top-start="tooltipText"
      :icon="progessIcon"
      class="text-n-slate-10"
    />
    <Icon
      v-else
      v-tooltip.top-start="tooltipText"
      :icon="statusIcon"
      :class="statusColor"
      class="size-[14px]"
    />
    <span
      v-if="effectiveStatus === MESSAGE_STATUS.DELIVERED"
      class="text-[10px] font-medium text-n-slate-10 leading-none"
    >{{ inlineLabel }}</span>
    <span
      v-else-if="effectiveStatus === MESSAGE_STATUS.READ"
      class="text-[10px] font-medium text-[#7EB6FF] leading-none"
    >{{ inlineLabel }}</span>
  </span>
</template>
